import { Dialog } from '@base-ui/react/dialog';
import { zodResolver } from '@hookform/resolvers/zod';
import { BadgeCheck, X } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useAmpli } from '@/ampli';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

import useDirectoryAgentClaimRequest from '../hooks/use-directory-agent-claim-request';

const claimRequestSchema = z.object({
  email: z.email('Enter a valid email address.'),
});

type ClaimRequestValues = z.infer<typeof claimRequestSchema>;

type DirectoryAgentClaimRequestDialogProps = {
  agentId: string;
  agentName: string;
  agentSlug: string;
  triggerLabel?: string;
};

const DirectoryAgentClaimRequestDialog = ({
  agentId,
  agentName,
  agentSlug,
  triggerLabel = 'Claim this profile',
}: DirectoryAgentClaimRequestDialogProps) => {
  const ampli = useAmpli();
  const claimRequest = useDirectoryAgentClaimRequest({ agentId, agentSlug });
  const form = useForm<ClaimRequestValues>({
    resolver: zodResolver(claimRequestSchema),
    defaultValues: { email: '' },
  });

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      return;
    }

    ampli((client) =>
      client.directoryAgentClaimOpened({ agent_slug: agentSlug }),
    );
  };

  const handleSubmit = async ({ email }: ClaimRequestValues) => {
    const error = await claimRequest.requestClaim(email);
    if (!error) {
      return;
    }

    if (error === 'VERIFICATION_FAILED') {
      form.setError('root', {
        message: 'Please complete the check and try again.',
      });
      return;
    }

    if (error === 'AGENT_NOT_FOUND') {
      form.setError('root', {
        message: 'This profile is no longer available to claim.',
      });
      return;
    }

    form.setError('root', {
      message: 'Something went wrong. Please try again.',
    });
  };

  return (
    <Dialog.Root onOpenChange={handleOpenChange}>
      <Dialog.Trigger className="inline-flex h-11 items-center justify-center gap-2 rounded-full border border-line-strong bg-white px-5 text-sm font-medium text-ink transition-[background-color,transform] duration-150 ease-out hover:bg-chip active:scale-[0.98] motion-reduce:transition-none">
        <BadgeCheck aria-hidden="true" className="size-4" />
        {triggerLabel}
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-40 bg-ink/35 backdrop-blur-sm transition-opacity duration-200 ease-out data-[ending-style]:opacity-0 data-[starting-style]:opacity-0 motion-reduce:transition-none" />
        <Dialog.Viewport className="fixed inset-0 z-50 flex items-center justify-center p-5">
          <Dialog.Popup className="surface-card relative w-full max-w-md p-6 transition-[transform,opacity] duration-200 ease-out data-[ending-style]:scale-95 data-[ending-style]:opacity-0 data-[starting-style]:scale-95 data-[starting-style]:opacity-0 motion-reduce:transition-none">
            <Dialog.Close
              aria-label="Close"
              className="absolute top-4 right-4 flex size-10 items-center justify-center rounded-full text-ink-muted hover:bg-chip hover:text-ink"
            >
              <X aria-hidden="true" className="size-5" />
            </Dialog.Close>
            {claimRequest.isSubmitted ? (
              <div className="flex flex-col gap-3 pr-8" role="status">
                <Dialog.Title className="m-0 text-2xl font-medium tracking-[-0.025em]">
                  Request received
                </Dialog.Title>
                <Dialog.Description className="m-0 leading-relaxed text-ink-soft">
                  We’ll be in touch about claiming {agentName}’s profile.
                </Dialog.Description>
              </div>
            ) : (
              <Form {...form}>
                <form
                  className="flex flex-col gap-5"
                  noValidate
                  onSubmit={form.handleSubmit(handleSubmit)}
                >
                  {claimRequest.verificationWidget}
                  <div className="flex flex-col gap-2 pr-8">
                    <Dialog.Title className="m-0 text-2xl font-medium tracking-[-0.025em]">
                      Claim {agentName}
                    </Dialog.Title>
                    <Dialog.Description className="m-0 leading-relaxed text-ink-soft">
                      Leave your work email and we’ll get in touch to verify
                      you, so you can correct details and confirm your fees.
                      This does not create an account.
                    </Dialog.Description>
                  </div>
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Work email</FormLabel>
                        <FormControl>
                          <input
                            autoComplete="email"
                            className="h-[52px] w-full rounded-xl border border-line-strong bg-white px-4 text-base text-ink"
                            placeholder="you@agency.co.uk"
                            type="email"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {form.formState.errors.root ? (
                    <p className="m-0 text-sm text-danger">
                      {form.formState.errors.root.message}
                    </p>
                  ) : null}
                  <button
                    className="button-primary h-12 disabled:pointer-events-none disabled:cursor-wait disabled:opacity-70"
                    disabled={
                      form.formState.isSubmitting || claimRequest.isRequesting
                    }
                    type="submit"
                  >
                    Request to claim profile
                  </button>
                </form>
              </Form>
            )}
          </Dialog.Popup>
        </Dialog.Viewport>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default DirectoryAgentClaimRequestDialog;
